import { useGlobalAction } from "@gadgetinc/react";
import clsx from "clsx";
import { useEffect } from "react";
import { Link } from "react-router";
import { api } from "../api";
import LoadingText from "./LoadingText";
import { Divider } from "./tailwind/divider";
import { Text } from "./tailwind/text";
import { Tooltip } from "./Tooltip";
import {
  HandThumbDownIcon,
  HandThumbUpIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowTopRightOnSquareIcon,
  Bars2Icon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import { Badge } from "./tailwind/badge";
import { formatDateTime } from "../functions/date-utils";
import { restrictionLabels } from "./dashboard/utils-dashboard/restrictionLabels";
import { Button } from "./tailwind/button";
import { useFindMany } from "@gadgetinc/react";
import { useState } from "react";

const limitLabels = {
  TIER_250: "250",
  "TIER_0.25K": "250",
  TIER_1K: "1000",
  TIER_2K: "2000",
  TIER_10K: "10k",
  TIER_100K: "100k",
  TIER_UNLIMITED: "Unlimited",
};

const qualityLabels = {
  GREEN: "High",
  YELLOW: "Medium",
  RED: "Low",
};

const qualityTooltips = {
  High: "Best quality score. It means that most of your contacts are happy with your messages. You can send more messages.",
  Medium:
    "Medium quality score. It means that some of your contacts are not happy with your messages. You can still send messages, but you should pay attention to content and segmentation.",
  Low: "Low quality score. It means that too many of your contacts are not happy with your messages. You should not send campaigns to new contacts.",
};

export function WhatsappQualityScoreBanner({ customer, className, isMobile }) {
  const isRestricted = new Date(customer.restrictionDate) > new Date();
  const [isCheckingLoading, setIsCheckingLoading] = useState(false);
  const [lastCheckPaymentMethodAt, setLastCheckPaymentMethodAt] = useState(
    customer.lastCheckPaymentMethodAt || null,
  );

  const [{ data: lastMessageSent }] = useFindMany(api.message, {
    filter: {
      customerId: { equals: customer.id },
      sentAt: { isSet: true },
      failedReason: { equals: null },
    },
    live: true,
    sort: {
      updatedAt: "Descending",
    },
    select: {
      sentAt: true,
    },
    first: 1,
  });

  const [isDisconnetedPaymentMethod, setIsDisconnetedPaymentMethod] =
    useState(null);

  useEffect(() => {
    console.log(lastMessageSent);
    if (customer?.lastMessageFailedForPaymentIssueAt && lastMessageSent?.[0]) {
      if (
        new Date(customer.lastMessageFailedForPaymentIssueAt) >
        new Date(lastMessageSent[0].sentAt)
      ) {
        setIsDisconnetedPaymentMethod(true);
      } else {
        setIsDisconnetedPaymentMethod(false);
        setLastCheckPaymentMethodAt(null);
        if (customer.lastCheckPaymentMethodAt) {
          api.customer.update(customer.id, {
            lastCheckPaymentMethodAt: null,
          });
        }
      }
      setIsDisconnetedPaymentMethod(
        new Date(customer.lastMessageFailedForPaymentIssueAt) >
          new Date(lastMessageSent[0].sentAt),
      );
    }
  }, [customer, lastMessageSent]);

  const [{ data, fetching }, getWhatsAppMessagingLimitTier] = useGlobalAction(
    api.getWhatsAppMessagingLimitTier,
  );

  const [{ data: data360, fetching: fetching360 }, get360DialogMessagingLimit] =
    useGlobalAction(api.get360DialogMessagingLimit);

  useEffect(() => {
    if (!customer) {
      return;
    }

    const { id, waAccessToken, apiKey360Dialog } = customer;
    if (id && waAccessToken) {
      getWhatsAppMessagingLimitTier({ customerId: id });
    }
    if (id && apiKey360Dialog) {
      get360DialogMessagingLimit({ customerId: id });
    }
  }, [customer?.id]);

  function LinkSimpleIcon() {
    return (
      <svg
        fill="#000000"
        viewBox="0 0 256 256"
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
      >
        <path d="M79.97266,119.99219h96a8,8,0,0,1,0,16h-96a8,8,0,0,1,0-16Zm23.99316,48.0039h-40a40,40,0,0,1,0-80h40a8,8,0,0,0,0-16h-40a56,56,0,0,0,0,112h40a8,8,0,0,0,0-16Zm88.00684-96h-40a8,8,0,0,0,0,16h40a40,40,0,0,1,0,80h-40a8,8,0,0,0,0,16h40a56,56,0,0,0,0-112Z"></path>
      </svg>
    );
  }

  let badgeColor = null;

  if (data360?.quality === "High" || data?.quality === "GREEN") {
    badgeColor = "lime";
  } else if (data360?.quality === "Medium" || data?.quality === "YELLOW") {
    badgeColor = "amber";
  } else if (data360?.quality === "Low" || data?.quality === "RED") {
    badgeColor = "rose";
  }

  const [, getWhatsAppTemplates] = useGlobalAction(api.getWhatsAppTemplates, {
    limit: "5000",
    templateName: "test_payment_method_simio",
  });

  const handleCheckPaymentMethod = async () => {
    setIsCheckingLoading(true);
    setLastCheckPaymentMethodAt(new Date());
    api.customer.update(customer.id, {
      lastCheckPaymentMethodAt: new Date(),
    });
    const templateName = "test_payment_method_simio";

    const templatesTemp = await getWhatsAppTemplates({
      limit: "5000",
      templateName: "test_payment_method_simio",
    });
    if (templatesTemp.error) {
      console.error("Error fetching templates:", templatesTemp.error);
      setIsCheckingLoading(false);
      return;
    }
    const { data: templateData360 } = templatesTemp;
    const templates = templateData360?.waba_templates ?? templateData360;

    let simpleTemplate = templates?.find(
      (t) => t.name === templateName && t.status === "APPROVED",
    );

    if (!simpleTemplate) {
      const submittedTemplate = {
        name: templateName,
        category: "UTILITY",
        language: "fr",
        components: [
          {
            type: "BODY",
            text: "This is a test message to check payment status on WhatsApp.",
          },
        ],
      };

      try {
        await api.sumbitTemplateTo360Dialog({
          template: JSON.stringify(submittedTemplate),
          customerId: customer.id,
        });

        console.warn(
          "Template de test soumis, il faut attendre la validation Meta avant d'envoyer le message.",
        );

        let approvedTemplate = null;
        for (let i = 0; i < 30; i++) {
          await new Promise((res) => setTimeout(res, 5000));

          const refreshedResult = await getWhatsAppTemplates({
            limit: "5000",
          });
          if (refreshedResult.error) {
            console.error("Error fetching templates in retry loop:", refreshedResult.error);
            break;
          }
          const refreshedTemplates =
            refreshedResult.data?.waba_templates ?? refreshedResult.data;

          approvedTemplate = refreshedTemplates?.find(
            (t) => t.name === templateName && t.status === "APPROVED",
          );

          if (approvedTemplate) break;
        }

        if (!approvedTemplate) {
          console.error(
            "Le template n'a pas été approuvé dans le délai imparti.",
          );
          return;
        }

        simpleTemplate = approvedTemplate;
        console.log("Template approuvé :", simpleTemplate);
      } catch (e) {
        console.error("Erreur en soumettant le template de test :", e);
        return;
      }
    }

    try {
      let whatsappTemplate;

      const existingWhatsappTemplate = await api.whatsappTemplate.findMany({
        first: 1,
        filter: {
          whatsappTemplateName: { equals: templateName },
        },
        select: { id: true },
      });
      if (existingWhatsappTemplate.length > 0) {
        whatsappTemplate = existingWhatsappTemplate[0];
      } else {
        const newWhatsappTemplate = await api.whatsappTemplate.create({
          whatsappTemplateName: simpleTemplate.name,
        });
        whatsappTemplate = newWhatsappTemplate;
      }

      await api.sendWhatsAppTemplate({
        phoneNumber: "33650689043", // astreinte
        whatsappTemplateId: whatsappTemplate.id,
        customerId: customer.id,
        isForced: true,
      });

      console.log("Message test envoyé");
      setIsCheckingLoading(false);
    } catch (e) {
      console.error("Erreur en envoyant le template test :", e);
    }
  };

  return (
    <div
      className={clsx(
        className,
        "border rounded-xl p-3 shadow bg-white flex flex-col justify-between",
      )}
    >
      {(!isRestricted || (isRestricted && isDisconnetedPaymentMethod)) && (
        <div>
          <Text className="text-lg font-bold mb-2">
            {`WhatsApp phone number${customer.phoneNumber ? `: +${customer.phoneNumber}` : ""}`}
          </Text>
          <Divider />
        </div>
      )}

      {fetching || fetching360 ? (
        <div className="h-full flex flex-col justify-center">
          <LoadingText className="h-[24px]" />
        </div>
      ) : !customer.phoneNumber ? (
        <div className="flex items-center h-full">
          <div className="w-6 h-4 flex items-center justify-center border-2 border-black rounded-sm">
            <XMarkIcon className="w-4 h-4 " />
          </div>
          <Link
            rel="noopener noreferrer"
            to="/onboarding"
            className="ml-2 hover:underline text-lg"
          >
            Connect WhatsApp
          </Link>
          <ChevronRightIcon className="w-5 h-5" />
        </div>
      ) : isDisconnetedPaymentMethod ? (
        <div className="h-full flex flex-col justify-between">
          <div className="flex items-start mt-2">
            <ExclamationTriangleIcon className="h-16 w-16 text-red-700 mr-2" />
            <div className="ml-2">
              <Text className="flex items-center">
                Payment method disconnected
              </Text>
              <div className="mt-1 text-sm">
                Please go to the{" "}
                <a
                  href="https://business.facebook.com/billing_hub/payment_methods"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Meta Business Manager billing page
                </a>{" "}
                and update your credit card.
              </div>
              {lastCheckPaymentMethodAt && !isCheckingLoading && (
                <Text className="text-xs">{`Last check done the ${formatDateTime(
                  new Date(lastCheckPaymentMethodAt),
                  "shortTimeLongDate",
                )}`}</Text>
              )}
            </div>
          </div>

          <div className="w-full flex justify-end items-end">
            <Button
              color="secondaryButton"
              onClick={handleCheckPaymentMethod}
              loading={isCheckingLoading}
            >
              Check payment method
            </Button>
          </div>
        </div>
      ) : isRestricted ? (
        <div className="h-full flex flex-col justify-between">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-700 mr-2" />
            <div className="ml-2">
              <Text className="flex items-center">
                {`Your account is restricted until ${formatDateTime(
                  new Date(customer.restrictionDate),
                  "longDate",
                )}`}
              </Text>
              {customer.violationType && (
                <Badge className="mt-2">
                  {`Violation type: ${customer.violationType}`}
                </Badge>
              )}
            </div>
          </div>
          <Divider />
          <div className=" flex mt-1 justify-center">
            <Text className="text-xs">Details:</Text>
            {customer.restrictionInfo?.length > 0 && (
              <Text className="text-xs ml-2">
                {(() => {
                  const reasons = customer.restrictionInfo
                    .map((i) => restrictionLabels[i.restriction_type])
                    .filter(Boolean);

                  if (reasons.length === 1) {
                    return reasons[0].toLowerCase();
                  }

                  const last = reasons.pop();
                  return `You cannot ${reasons.map((r) => r.toLowerCase()).join(", ")} or ${last.toLowerCase()}.`;
                })()}
              </Text>
            )}
          </div>
          <div className="w-full flex justify-between items-end">
            <Link
              target="_blank"
              rel="noopener noreferrer"
              to={`https://business.facebook.com/latest/whatsapp_manager/phone_numbers/${customer?.wabaId ? "?asset_id=" + customer.wabaId : ""}`}
              className="underline text-xs flex items-center"
            >
              More details on Meta Business Manager
              <ArrowTopRightOnSquareIcon className="w-5 h-5 ml-1" />
            </Link>
            {!isMobile && (
              <Button
                color="secondaryButton"
                onClick={() => {
                  if (
                    window.alcmeonInMessage &&
                    typeof window.alcmeonInMessage.openChat === "function"
                  ) {
                    window.alcmeonInMessage.openChat();
                  } else {
                    console.warn("Alcmeon chat is not ready yet");
                  }
                }}
              >
                Contact us
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-5 justify-between">
            <Badge
              className="my-0"
              {...(badgeColor ? { color: badgeColor } : {})}
            >
              <div className="flex gap-2 items-center m-3">
                {((data && !data?.quality) ||
                  (data360 && !data360?.quality)) && (
                  <>
                    <LinkSimpleIcon />
                  </>
                )}
                {(data?.quality === "GREEN" || data360?.quality === "High") && (
                  <HandThumbUpIcon className="h-7" />
                )}

                {(data?.quality === "YELLOW" ||
                  data360?.quality === "Medium") && (
                  <Bars2Icon className="w-5 h-5" />
                )}
                {(data?.quality === "RED" || data360?.quality === "Low") && (
                  <HandThumbDownIcon className="h-7" />
                )}

                <Text>
                  {!data?.quality && !data360?.quality
                    ? "Connected"
                    : data?.quality === "Low" || data360?.quality === "RED"
                      ? "Flagged"
                      : data
                        ? qualityLabels[data?.quality]
                        : data360?.quality}
                </Text>
                {!isMobile && (
                  <div className="text-gray-500">
                    <Tooltip
                      content={
                        data?.quality === "Low" || data360?.quality === "RED"
                          ? qualityTooltips.Low
                          : data360?.quality === "High" ||
                              data?.quality === "GREEN"
                            ? qualityTooltips.High
                            : data360?.quality === "Medium" ||
                                data?.quality === "YELLOW"
                              ? qualityTooltips.Medium
                              : "WhatsApp quality score based on contacts interactions."
                      }
                      size="4"
                    />
                  </div>
                )}
              </div>
            </Badge>
            <div className="flex gap-2 items-center">
              <Text className="text-xl text-purple font-bold">
                {limitLabels[(data || data360)?.messaging_limit_tier]}{" "}
              </Text>
              <Text className="text-md text-purple">
                conversations / 24 hours
              </Text>
              {!isMobile && (
                <div className="text-gray-500">
                  <Tooltip
                    content="Max number of users you can message in 24h."
                    size="4"
                  ></Tooltip>
                </div>
              )}
            </div>
          </div>
          <Link
            target="_blank"
            rel="noopener noreferrer"
            to={`https://business.facebook.com/latest/whatsapp_manager/phone_numbers/${customer?.wabaId ? "?asset_id=" + customer.wabaId : ""}`}
            className="underline text-xs flex items-center"
          >
            See more on Meta Business Manager
            <ArrowTopRightOnSquareIcon className="w-5 h-5 ml-1" />
          </Link>
        </>
      )}
    </div>
  );
  // }
}
